"use client";

import Link from "next/link";

import { TwinLiveMark } from "@/components/app/twin/twin-live-mark";
import { Button, buttonVariants } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import { ViewGuestSessionTooltip, VIEW_GUEST_SESSION_REASON } from "./view-guest-session-control";

/**
 * The Twin's mark in its disc: the glyph for starting a session, wherever one
 * starts. It breathes only while a session is being started.
 */
export function AskTwinMark({ active = false }: { active?: boolean }) {
  return (
    <span aria-hidden className="size-3.5 shrink-0 overflow-hidden rounded-full">
      <TwinLiveMark bleed active={active} className="size-full" />
    </span>
  );
}

export type AskTwinLinkProps = {
  href: string;
  title: string;
  compact?: boolean;
  size?: "xs" | "sm";
  className?: string;
};

/**
 * "Ask" beside the Twin's mark, as an outline button; disabled, with its
 * reason, for a view guest. It reads no session, so the marketing site's
 * product shots draw it too; the app's `AskTwinLink` supplies `viewGuest`.
 */
export function AskTwinLinkView({
  href,
  title,
  compact = false,
  size = "sm",
  className,
  viewGuest = false,
}: AskTwinLinkProps & { viewGuest?: boolean }) {
  if (viewGuest) return (
    <ViewGuestSessionTooltip disabled>
      <Button disabled variant="outline" size={size} className={cn("shrink-0", className)}
        aria-label={title} title={VIEW_GUEST_SESSION_REASON}>
        <AskTwinMark />
        <span className={compact ? "hidden sm:inline" : undefined}>Ask</span>
      </Button>
    </ViewGuestSessionTooltip>
  );
  return (
    <Link
      href={href}
      aria-label={title}
      title={title}
      className={cn(buttonVariants({ variant: "outline", size }), "shrink-0", className)}
    >
      <AskTwinMark />
      <span className={compact ? "hidden sm:inline" : undefined}>Ask</span>
    </Link>
  );
}
